'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import IconFont from '@/components/ui/iconFont';
import StatusBadge from './status-badge';
// import numberbgImg from '../../images/numberbg.png';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useRewardCountDown } from '../../../hooks/useRewardCountDown';
import type { UIItem } from '../adapter';
import { UI_CONFIG } from '../adapter';
import Image from 'next/image';
import cn from '@/utils/classnames';
import { track } from '@/utils/track';
import { useAtom } from 'jotai';
import { backpackActiveTabAtom } from '@/features/reward/store/backpack-tab';
import { TabOption } from './secondary-tabs';

interface CouponCardProps {
  tabs: TabOption[];
  className?: string;
  item: UIItem;
  onUse?: () => void;
}

const propStateMap: Record<UIItem['uiStatus'], number> = {
  ACTIVE: 1,
  EXPIRING: 1,
  USED: 2,
  NOT_STARTED: 3,
  EXPIRED: 4,
};

export default function CouponCard({ tabs, className, item, onUse }: CouponCardProps) {
  const t = useTranslations('reward');
  const ui = UI_CONFIG[item.uiStatus];
  const [activeTab, _] = useAtom(backpackActiveTabAtom);

  const { countdownStr } = useRewardCountDown({ targetDate: item.countdownTarget ?? 0 });

  // const textShadow = `rgba(4,152,16,0.5) -1px -1px, rgba(4,152,16,0.5) 1px -1px, rgba(4,152,16,0.5) -1px 1px, rgba(4,152,16,0.5) 1px 1px`;

  const timeText = useMemo(() => {
    if (!ui?.showCountdown || !countdownStr) return undefined;
    return item.uiStatus === 'NOT_STARTED'
      ? t('activatesIn', { time: countdownStr })
      : t('expiresIn', { time: countdownStr });
  }, [ui?.showCountdown, countdownStr, item.uiStatus, t]);

  const buildPropTrackParams = (item: UIItem, buttonName: string) => ({
    current_page: tabs?.find(t => t.value === activeTab)?.label_en ?? activeTab,
    prop_id: item.item_id,
    // 需要英文名称
    prop_name: item.prop_name,
    // 1待使用 2已使用 3不可使用 4已过期
    prop_state: propStateMap[item.uiStatus],
    // 获取时间
    obtain_time: item.grant_at,
    valid_end_time: item.expire_at,
    // 最近获取或刷新时间，前端上报使用
    use_time: item.effective_at,
    button_name: buttonName,
  });

  const handleHelp = (item: UIItem) => {
    // 执行内部 onHelp 逻辑
    track('prop_button_click', {
      ...buildPropTrackParams(item, 'detail'),
    });
  };

  return (
    <div className={`relative rounded-[8px] overflow-hidden ${className}`}
      style={{
        background: 'linear-gradient(to bottom right, #1D3657 0%, #1B2131 65%, #1B2131 100%)',
      }}
    >
      {/* {item.stack_count && (
        <div
          className="h-[19px] leading-4 pr-1 absolute left-0 top-0 text-white text-xs font-semibold bg-cover bg-center bg-no-repeat"
          style={{
            ...getBadgeBackgroundStyle(numberbgImg.src, 6, 16),
            textShadow,
          }}
        >
          <div className='pl-[2px]'>x{item.stack_count}</div>
        </div>
      )} */}

      {Boolean(item.is_near_expire) ? (
        <StatusBadge badge={'expiring'} width={10} widthImage={16} />
      ) :
        Boolean(item.is_red_dot) && (
          <StatusBadge badge={'new'} width={10} widthImage={16} />
        )
      }

      <div className="flex items-center gap-3 pl-[9px] pr-[15px] py-4">
        {
          item?.icon ?
            <Image className='flex-shrink-0 max-h-[53px] rounded-xl flex items-center justify-center' width={53} height={53} src={item?.icon} alt=''></Image> :
            <div className='w-[53] h-53'></div>
        }

        <div className="flex flex-col items-start flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-white text-xs font-medium">
              {item.name}
            </span>
            {item.sub_name && (
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" onClick={() => handleHelp(item)}>
                    <IconFont name="help" size={14} />
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <div>{item.sub_name}</div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <span className="h-[12px] leading-[12px] bg-[rgba(0,9,19,0.3)] rounded-md px-1.5 mt-1 text-[9px] font-medium text-[rgba(160,207,255,0.6)]">
            {item.source?.toUpperCase()}
          </span>
          <div className="pt-2 text-xss text-tertiary font-medium">
            {timeText}
          </div>
        </div>

        <Button
          disabled={ui?.disabled}
          size="sm"
          variant={ui?.dimmed ? 'default' : 'primary'}
          className={cn("min-w-[90px]", ui?.dimmed ? 'text-[#7A879A] !font-medium bg-[rgba(142,210,255,0.04)] disabled:opacity-1' : '')}
          onClick={ui?.disabled ? undefined : onUse}
        >
          {t(ui?.buttonKey as Parameters<typeof t>[0])}
        </Button>
      </div>
    </div>
  );
}